/**
 * Public Session Manager
 * 
 * Manages sessions for public share links (research/proposal).
 * Supports per-token session storage and works with httpOnly cookies.
 */

export interface StoredSession {
  token: string;
  sessionId: string;
  email: string;
  expiresAt: string;
  isPlatformUser?: boolean;
}

/**
 * Get storage key for a specific token (allows multiple sessions)
 */
function getStorageKey(token: string): string {
  return `moniker_public_session_${token.substring(0, 16)}`;
}

/**
 * Get stored session for a specific token
 */
export function getStoredSession(token: string): StoredSession | null {
  try {
    const key = getStorageKey(token);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const session: StoredSession = JSON.parse(stored);
    
    // Verify session is for this token
    if (session.token !== token) {
      localStorage.removeItem(key);
      return null;
    }
    
    // Check if expired
    if (new Date(session.expiresAt) < new Date()) {
      localStorage.removeItem(key);
      return null;
    }
    
    return session;
  } catch {
    return null;
  }
}

/**
 * Store session for a specific token
 */
export function storeSession(session: StoredSession): void {
  try {
    const key = getStorageKey(session.token);
    localStorage.setItem(key, JSON.stringify(session));
  } catch {
    // Ignore storage errors (e.g., quota exceeded, private browsing)
  }
}

/**
 * Clear session for a specific token
 */
export function clearSession(token: string): void {
  try {
    const key = getStorageKey(token);
    localStorage.removeItem(key);
  } catch {
    // Ignore errors
  }
}

/**
 * Clear all public sessions (cleanup utility)
 */
export function clearAllSessions(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('moniker_public_session_')) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore errors
  }
}

/**
 * Get session ID from httpOnly cookie (if available)
 * Falls back to stored session in localStorage
 * 
 * Note: JavaScript cannot read httpOnly cookies, so we rely on
 * the server to validate the cookie. This function is mainly
 * for getting metadata (email, expiresAt) from localStorage.
 */
export function getSessionMetadata(token: string): Omit<StoredSession, 'sessionId'> | null {
  const session = getStoredSession(token);
  if (!session) return null;
  
  return {
    token: session.token,
    email: session.email,
    expiresAt: session.expiresAt,
    isPlatformUser: session.isPlatformUser,
  };
}

